import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Container,
  Dropdown,
  DropdownToggle,
  UncontrolledTooltip,
  DropdownMenu,
  Collapse,
} from "reactstrap";
import ViewSkeleton from "../../../../Components/Common/ViewSkeleton";
import CommonOffcanvasLayout from "../../../../Components/Common/CommonOffcanvasLayout";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import EditStudyDetailsForm from "./EditStudyForm";
import styles from "../css/studydetailsview.module.scss";
import viewStyles from "../../../../assets/scss/common/viewDetails.module.scss";
import tableStyle from "../../../../assets/scss/common/table.module.scss";
import { useStudyDetailsService } from "../../../../hooks/useCommonQuery";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import Reason, { ReasonSubmitPayload } from "../../../../common/utilities/Reason/Reason";
import { usePermission } from "../../../../context/PermissionContext";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "../../../../common/constants/PermissionConstants";
import { StudyFormValues, StudySection, ViewDetailsProps } from "../helpers/study.types";
import { labels, normalizeFormValues, normalizeStudyChecklists, renderStudyValue, tabConfig, truncateWithTooltip, viewchecklistscolumns } from "../helpers/studyconstant";
import { showToastMessage } from "common/service/CommonService";
import { formatDate } from "helpers/constant";
import { loadCodelists, loadSponsors, loadStudyDesign } from "api/modules/common.option";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { activitycolumns } from "modules/Clinical/AmbulatoryVisit/helper/ambulatoryvisitconstants";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";

/* ---------- Sub-component for Checklist Assignment View Mode ---------- */
const ChecklistAssignmentViewSection: React.FC<{ studyData: any }> = ({ studyData }) => {
  const numPeriods = Number(studyData?.numberofperiods) || 1;

  const [selectedScope, setSelectedScope] = useState<"pre_enrollment" | "period_based">("pre_enrollment");
  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [selectedType, setSelectedType] = useState<number | null>(null);

  const allStudyChecklists: any[] = useMemo(() => {
    if (!studyData || !studyData.studychecklists) return [];
    return normalizeStudyChecklists(studyData.studychecklists);
  }, [studyData]);

  const hasInclusionExclusionCriteria = useMemo(() => {
    return allStudyChecklists.some((item: any) => {
      const text = String(
        item.checklisttypename || item.checklisttype_name || item.checklisttypetext || item.checklisttype_text || ""
      ).toLowerCase();
      return text.includes("inclusion") || text.includes("exclusion");
    });
  }, [allStudyChecklists]);

  const isPreEnrollmentType = (text: string) => {
    const t = text.toLowerCase();
    return (
      t.includes("inclusion") ||
      t.includes("exclusion") ||
      t.includes("pre enrollment") ||
      t.includes("pre-enrollment")
    );
  };

  const rawLists = useMemo(() => {
    if (!allStudyChecklists.length) return [];

    if (hasInclusionExclusionCriteria && selectedScope === "pre_enrollment") {
      return allStudyChecklists.filter((item: any) => {
        const text = String(
          item.checklisttypename || item.checklisttype_name || item.checklisttypetext || item.checklisttype_text || ""
        );
        return isPreEnrollmentType(text);
      });
    }

    let targetPeriodId: number | null = null;
    if (Array.isArray(studyData.periods)) {
      const pObj = studyData.periods.find((p: any) => Number(p.period_no) === selectedPeriod);
      if (pObj) targetPeriodId = Number(pObj.period_id);
    }

    return allStudyChecklists.filter((item: any) => {
      const text = String(
        item.checklisttypename || item.checklisttype_name || item.checklisttypetext || item.checklisttype_text || ""
      );
      if (hasInclusionExclusionCriteria && isPreEnrollmentType(text)) {
        return false;
      }
      const itemSort = Number(item.period_sort);
      if (itemSort === selectedPeriod) return true;
      if (targetPeriodId !== null && Number(item.periodid) === targetPeriodId) return true;
      return false;
    });
  }, [allStudyChecklists, selectedPeriod, studyData, hasInclusionExclusionCriteria, selectedScope]);

  const typeOptions = useMemo(() => {
    const map = new Map<number, { label: string; count: number }>();
    rawLists.forEach((item: any) => {
      const typeVal = Number(item.checklisttype);
      const label =
        item.checklisttypename ||
        item.checklisttype_name ||
        item.checklisttypetext ||
        item.checklisttype_text ||
        `Type ${typeVal}`;
      if (typeVal) {
        const existing = map.get(typeVal);
        map.set(typeVal, {
          label,
          count: (existing?.count || 0) + 1,
        });
      }
    });
    return Array.from(map.entries()).map(([value, info]) => ({
      value,
      label: info.label,
      count: info.count,
    }));
  }, [rawLists]);

  useEffect(() => {
    if (typeOptions.length > 0 && (!selectedType || !typeOptions.some((t: any) => t.value === selectedType))) {
      setSelectedType(typeOptions[0].value);
    }
  }, [typeOptions, selectedType, selectedScope]);

  const activeQuestions = useMemo(() => {
    if (!selectedType) return [];
    return rawLists.filter((item: any) => Number(item.checklisttype) === selectedType);
  }, [rawLists, selectedType]);

  const activeTypeLabel = typeOptions.find((t: any) => t.value === selectedType)?.label || "";

  return (
    <Col lg="12">
      {/* Scope Selector Chips (if Inclusion/Exclusion exist) */}
      {hasInclusionExclusionCriteria && (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Checklist Scope:</div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedScope("pre_enrollment")}
              className={`btn btn-sm ${
                selectedScope === "pre_enrollment" ? "btn-primary" : "btn-soft-secondary"
              } rounded-pill px-3 d-flex align-items-center gap-1`}
            >
              <i className="ri-file-text-line me-1" /> Enrollment Checklists
            </button>
            <button
              type="button"
              onClick={() => setSelectedScope("period_based")}
              className={`btn btn-sm ${
                selectedScope === "period_based" ? "btn-primary" : "btn-soft-secondary"
              } rounded-pill px-3 d-flex align-items-center gap-1`}
            >
              <i className="ri-calendar-line me-1" /> Study Period Checklists
            </button>
          </div>
        </div>
      )}

      {/* Period Selector (Only when Period-Based or no Inclusion/Exclusion) */}
      {(!hasInclusionExclusionCriteria || selectedScope === "period_based") && numPeriods > 1 && (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Select Period:</div>
          <div className="d-flex flex-wrap gap-2">
            {Array.from({ length: numPeriods }, (_, i) => i + 1).map((p) => {
              const isSelected = selectedPeriod === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSelectedPeriod(p)}
                  className={`btn btn-sm ${
                    isSelected ? "btn-primary" : "btn-soft-secondary"
                  } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  Period {p}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {typeOptions.length > 0 ? (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Checklist Types:</div>
          <div className="d-flex flex-wrap gap-2">
            {typeOptions.map((type: any) => {
              const isSelected = selectedType === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setSelectedType(type.value)}
                  className={`btn btn-sm ${
                    isSelected ? "btn-primary" : "btn-outline-secondary"
                  } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  <span>{type.label}</span>
                  <span className="badge bg-light text-primary rounded-circle ms-1">
                    {type.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-muted fs-14 py-2">No checklists configured for this view.</div>
      )}

      {activeQuestions.length > 0 && (
        <div className="mt-3">
          <h6 className="fw-bold text-dark mb-2">
            {activeTypeLabel} ({activeQuestions.length} Items)
          </h6>
          <CommonTableIndexWise
            columns={viewchecklistscolumns}
            data={activeQuestions}
            paginationShow={false}
            hideAction={true}
            showStatus={false}
          />
        </div>
      )}
    </Col>
  );
};

/* ---------- Sub-component for CRF Assignment View Mode ---------- */
const CRFAssignmentViewSection: React.FC<{ studyData: any }> = ({ studyData }) => {
  const numPeriods = Number(studyData?.numberofperiods) || 1;

  const [selectedPeriod, setSelectedPeriod] = useState<number>(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const { CategorySelectAllQuery } = useCRFService({
    ispagination: false,
  });

  const categoryLookup = useMemo(() => {
    const modules = CategorySelectAllQuery?.data?.data || [];
    const map = new Map<number, string>();
    modules.forEach((item: any) => {
      map.set(Number(item.moduleid), item.modulename);
    });
    return map;
  }, [CategorySelectAllQuery]);

  const rawLists = useMemo(() => {
    if (!studyData || !Array.isArray(studyData.studycrfs)) return [];

    let targetPeriodId: number | null = null;
    if (Array.isArray(studyData.periods)) {
      const pObj = studyData.periods.find((p: any) => Number(p.period_no) === selectedPeriod);
      if (pObj) targetPeriodId = Number(pObj.period_id);
    }

    return studyData.studycrfs.filter((item: any) => {
      const itemSort = Number(item.period_sort);
      if (itemSort === selectedPeriod) return true;
      if (targetPeriodId !== null && Number(item.periodid) === targetPeriodId) return true;
      return false;
    });
  }, [studyData, selectedPeriod]);

  const categoryOptions = useMemo(() => {
    const map = new Map<number, { label: string; count: number }>();
    rawLists.forEach((item: any) => {
      const catVal = Number(item.crfmodule || item.moduleid || item.crfcategory);
      if (catVal) {
        const label = categoryLookup.get(catVal) || item.modulename || item.categoryname || `Category ${catVal}`;
        const existing = map.get(catVal);
        map.set(catVal, {
          label,
          count: (existing?.count || 0) + 1,
        });
      }
    });
    return Array.from(map.entries()).map(([value, info]) => ({
      value,
      label: info.label,
      count: info.count,
    }));
  }, [rawLists, categoryLookup]);

  useEffect(() => {
    if (categoryOptions.length > 0 && (!selectedCategory || !categoryOptions.some((t: any) => t.value === selectedCategory))) {
      setSelectedCategory(categoryOptions[0].value);
    }
  }, [categoryOptions, selectedCategory]);

  const activeCRFs = useMemo(() => {
    if (!selectedCategory) return [];
    return rawLists.filter((item: any) => Number(item.crfmodule || item.moduleid || item.crfcategory) === selectedCategory);
  }, [rawLists, selectedCategory]);

  const activeCategoryLabel = categoryOptions.find((t: any) => t.value === selectedCategory)?.label || "";

  const crfViewColumns = [
    {
      key: "crfname",
      label: "CRF Name",
      render: (val: string, row: any) => (
        <span className="fw-medium text-dark">
          {val || row.crfname || row.crfName || row.name || `CRF #${row.crfid}`}
        </span>
      ),
    },
  ];

  return (
    <Col lg="12">
      {numPeriods > 1 && (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">Select Period:</div>
          <div className="d-flex flex-wrap gap-2">
            {Array.from({ length: numPeriods }, (_, i) => i + 1).map((p) => {
              const isSelected = selectedPeriod === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setSelectedPeriod(p)}
                  className={`btn btn-sm ${
                    isSelected ? "btn-primary" : "btn-soft-secondary"
                  } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  Period {p}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {categoryOptions.length > 0 ? (
        <div className="mb-3">
          <div className="fw-medium text-muted mb-2 fs-13">CRF Categories:</div>
          <div className="d-flex flex-wrap gap-2">
            {categoryOptions.map((cat: any) => {
              const isSelected = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`btn btn-sm ${
                    isSelected ? "btn-primary" : "btn-outline-secondary"
                  } rounded-pill px-3 d-flex align-items-center gap-1`}
                >
                  <span>{cat.label}</span>
                  <span className="badge bg-light text-primary rounded-circle ms-1">
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-muted fs-14 py-2">No CRFs configured.</div>
      )}

      {activeCRFs.length > 0 && (
        <div className="mt-3">
          <h6 className="fw-bold text-dark mb-2">
            {activeCategoryLabel} ({activeCRFs.length} Items)
          </h6>
          <CommonTableIndexWise
            moduleName={PERMISSION_MODULES.STUDY}
            columns={crfViewColumns}
            data={activeCRFs}
            paginationShow={false}
            hideAction={true}
            showStatus={false}
          />
        </div>
      )}
    </Col>
  );
};

/* ---------- Component ---------- */
const ViewStudyDetails: React.FC<ViewDetailsProps> = ({
  studyid,
  protocolno,
  isReviewMode,
  hideSidebar
}) => {
  // Query client
  const { hasPermission } = usePermission();
  const canEdit = hasPermission(PERMISSION_MODULES.STUDY, PERMISSION_ACTIONS.EDIT);
  const canDelete = hasPermission(PERMISSION_MODULES.STUDY, PERMISSION_ACTIONS.DEACTIVATE);

  const queryClient = useQueryClient();
  // Navigation
  const navigate = useNavigate();

  // Refs
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isManualScroll = useRef(false);
  // Study Service(s)
  const { SelectQuery, UpdateMutation, DeleteMutation } =
    useStudyDetailsService(null, null, studyid ?? undefined);

  const { data, isLoading, isError } = SelectQuery;
  //-------------------------------------------------------------------------------------------
  //--> Use State
  //-------------------------------------------------------------------------------------------

  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(1);
  const [activeSection, setActiveSection] = useState<StudySection>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  //Reason Modal
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [updatedRecord, setUpdatedRecord] = useState<Partial<StudyFormValues> | null>(null);
  // const [showAttachments, setShowAttachments] = useState<boolean>(false);
  const showAttachments = false; // Mocking as false since it's commented out
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [openVisitIndex, setOpenVisitIndex] = useState<number | null>(null);


  //-------------------------------------------------------------------------------------------
  //--> Use Effect
  //-------------------------------------------------------------------------------------------
  const study = data?.data[0];
  const isStudyActive =
    study?.isActive === true ||
    study?.isactive === true ||
    study?.isdraft === false ||
    study?.isdraft === "false" ||
    study?.isdraft === 0 ||
    study?.isdraft === "0";

  // Load codelists on mount to prevent missing labels on refresh
  useEffect(() => {
    const load = async () => {
        const result = await loadCodelists([
          "Country_OPTIONS",
          "Routeof_Administration_OPTIONS",
          "Light_Condition_OPTIONS",
          "Gender_OPTIONS",
          "Biological_matrix_OPTIONS",
          "Fasting_Condition_OPTIONS",
          "StudyType_OPTIONS",
          "SEQUENCE_NUMBER_OPTIONS",
        ]);

      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
    };
    load();
    loadSponsors();
    loadStudyDesign();
  }, []);

  // Scroll spy effect
  useEffect(() => {
    const handleScroll = () => {
      if (isManualScroll.current) return;

      if (!contentRef.current) return;

      const containerRect = contentRef.current.getBoundingClientRect();
      const offset = 150; // Increased slightly for better sensitivity

      let activeId = tabConfig[0].id;
      let candidateId = null;

      // 1. Precise Check: Which section is spanning the offset line?
      for (let i = 0; i < tabConfig.length; i++) {
        const section = sectionRefs.current[tabConfig[i].id];
        if (section) {
          const sectionRect = section.getBoundingClientRect();
          const relativeTop = sectionRect.top - containerRect.top;
          const relativeBottom = relativeTop + sectionRect.height;

          if (relativeTop <= offset && relativeBottom > offset) {
            candidateId = tabConfig[i].id;
            break;
          }
        }
      }

      if (candidateId !== null) {
        activeId = candidateId;
      } else {
        // 2. Fallback: Only use bottom detection if no section is active at the top line
        const isAtBottom =
          Math.abs(
            contentRef.current.scrollTop +
            contentRef.current.clientHeight -
            contentRef.current.scrollHeight,
          ) < 10;

        if (isAtBottom) {
          activeId = tabConfig[tabConfig.length - 1].id;
        }
      }

      setActiveTab(activeId);
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener("scroll", handleScroll);
      return () => contentElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleTabClick = (tabId: number): void => {
    isManualScroll.current = true; // Block spy logic

    setActiveTab(tabId);
    // setShowAttachments(false);

    const section = sectionRefs.current[tabId];
    const container = contentRef.current;

    if (section && container) {
      const containerRect = container.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const offset = 140;

      const targetScrollTop =
        scrollTop + (sectionRect.top - containerRect.top) - offset;

      container.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
    }

    // Increase timeout to ensure smooth scroll has completely settled
    setTimeout(() => {
      isManualScroll.current = false;
    }, 1000);
  };

  //-------------------------------------------------------------------------------------------
  //--> Toggle
  //-------------------------------------------------------------------------------------------

  const toggle = (): void => setIsOpen(!isOpen);
  // const toggleDeleteModal = (): void => setDeleteModal(!deleteModal);
  const toggleReasonModal = (): void => {
    setIsReasonModal((prev) => !prev);
  };
  const toggleDeleteModal = (): void => {
    setDeleteModal(!deleteModal);
    setShowDeleteReason(false);
  };
  const toggleDeleteReason = (): void => setShowDeleteReason(!showDeleteReason);
  const toggleEdit = (sectionName: StudySection | null = null): void => {
    if (isStudyActive && sectionName) return;
    setActiveSection(sectionName);
    setShowEdit((prev) => !prev);
  };

  //--> ON SAVE
  //-------------------------------------------------------------------------------------------

  const handleSave = (data: Partial<StudyFormValues>): void => {
    if (!study) return;

    const commonFields = ["studyid", "updatedby", "createdby", "userid"]
      .reduce((acc, key) => {
        if ((study as any)?.[key] !== undefined) {
          acc[key] = (study as any)[key];
        }
        return acc;
      }, {} as Record<string, any>);

    const updated: Partial<StudyFormValues> = {
      ...commonFields,
      ...data,
    };

    setUpdatedRecord(updated);
    toggleReasonModal();
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Update
  //-------------------------------------------------------------------------------------------

  const btnReasonClick = (reasonPayload: ReasonSubmitPayload) => {
    if (!updatedRecord) return;

    const formattedRecord: any = { ...updatedRecord };
  
    const payload: any = {
      ...formattedRecord,
      updatereason: reasonPayload.reason,
    };

    // Check if totalsubjects has changed
    if (
      study &&
      payload.totalsubjects !== undefined &&
      Number(study.totalsubjects) === Number(payload.totalsubjects)
    ) {
      delete payload.totalsubjects;
    }

    const normalizedValues = normalizeFormValues(payload);
    UpdateMutation.mutate(normalizedValues, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["study-select", studyid] });
        showToastMessage("success", response?.message);
        toggleReasonModal();
        toggleEdit(null);
        setUpdatedRecord(null);
        setActiveSection(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Delete
  //------------------------------------------------------------------------------------------

  const handleDeleteClick = (): void => {
    setDeleteModal(true);
  };

  const confirmDelete = (reasonPayload: ReasonSubmitPayload): void => {
    const id = reasonPayload.id ?? studyid;
    if (id == null) return;
    const payload = {
      id,
      updatereason: reasonPayload.reason,
    };
    DeleteMutation.mutate(payload, {
      onSuccess: (response) => {
        showToastMessage("success", response?.message);
        toggleDeleteModal();
        navigate("/master/study");
      },
      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  return (
    <>
      {/* Main Content: Sidebar + Content Area */}
      <div className={styles.studyDetailsContainer} style={(isReviewMode || hideSidebar) ? { height: '100%', background: 'transparent' } : {}}>
        {/* Left Sidebar */}
        {!isReviewMode && !hideSidebar && (
          <aside className={styles.sidebar} ref={sidebarRef}>
            <Card
              className="border-0 shadow-sm rounded-4 w-100 mb-0 overflow-hidden d-flex flex-column"
              style={{ height: "100%" }}
            >
              <CardBody
                className="p-0 d-flex flex-column"
                style={{ minHeight: 0, flex: 1 }}
              >
                <div className={styles.breadcrumbWrap}>
                  <BreadCrumb
                    items={[
                      { text: "Study", link: "/master/study" },
                      { text: "Study Details" },
                    ]}
                  />
                </div>
                <div className={`${styles.tabScrollArea} p-3 pt-0`}>
                  <ul className={styles.sidebarList}>
                    {tabConfig.map((section) => {
                      const isActive = activeTab === section.id && !showAttachments;
                      return (
                        <li
                          key={section.id}
                          id={`sidebar-tab-${section.id}`}
                          className={`px-3 py-2 rounded-3 d-flex align-items-center fw-medium ${!isActive ? viewStyles.sectionHeader : viewStyles.activeSection} `}
                          onClick={() => handleTabClick(section.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className={`${section.icon} fs-18 me-3`} style={{ color: isActive ? "#24465a" : "#878a99" }}></i>
                          <span className="flex-grow-1">{section.title}</span>
                          {isActive && <i className="ri-arrow-right-s-line fs-18 opacity-50"></i>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </CardBody>
            </Card>
          </aside>
        )}

        {/* Right Content Area */}
        <div 
          className={styles.contentArea} 
          ref={contentRef} 
          style={(isReviewMode || hideSidebar) ? { background: 'transparent' } : {}}
        >
          {!isReviewMode && !hideSidebar && (
            <div
              className={`d-flex justify-content-between align-items-center ${tableStyle.table_heading_container} ${styles.stickyHeader}`}
            >
              <h4 className="text-dark mb-0">
                <>
                  Study Management - Details{" "}
                  {protocolno && <span>( {protocolno} )</span>}
                </>
              </h4>
              {/* ✅ Hide action dropdown when attachments is shown */}
              {!showAttachments && !isReviewMode && canDelete && (
                <Dropdown className="p-0" isOpen={isOpen} toggle={toggle}>
                  <DropdownToggle
                    tag="a"
                    id="actionMenuToggle"
                    className="btn btn-icon btn-action btn-ghost-dark rounded-circle"
                  >
                    <i className="ri-more-fill fs-20"></i>
                  </DropdownToggle>
                  <UncontrolledTooltip placement="top" target="actionMenuToggle">
                    Action
                  </UncontrolledTooltip>
                  <DropdownMenu className="dropdown-menu-end">
                    {canDelete && (
                      <li>
                        <a
                          className="dropdown-item text-danger"
                          href="#"
                          onClick={() => handleDeleteClick()}
                        >
                          <i className="ri-delete-bin-line align-bottom me-2"></i>
                          Deactivate
                        </a>
                      </li>
                    )}
                  </DropdownMenu>
                </Dropdown>
              )}
            </div>
          )}

          <Container fluid className="p-3 pb-0">
            {isLoading || SelectQuery.isFetching ? (
              <ViewSkeleton sections={10} fieldsPerSection={6} />
            ) : (
              <Row className="g-3">
                {tabConfig.map((section, idx) => (
                  <Col
                    lg="12"
                    key={section.id}
                    className={idx === tabConfig.length - 1 ? "mb-3" : ""}
                  >
                    <div
                      ref={(el) => {
                        sectionRefs.current[section.id] = el;
                      }}
                    >
                      <Card className={`${viewStyles.reviewBox} h-100 mb-0`}>
                        <CardBody>
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <h6 className="fw-bold text-primary mb-0 d-flex align-items-center">
                                <i className={`${section.icon} me-2`}></i> {section.title}
                              </h6>
                              {!isReviewMode && canEdit && !isStudyActive && (
                                <i
                                  className="bx bxs-edit text-primary fs-5 cursor-pointer"
                                  onClick={() =>
                                    toggleEdit(section.title as StudySection)
                                  }
                                ></i>
                              )}
                            </div>
                          </div>

                          {/* Fields */}
                          <Row className="g-2 mt-1">
                            {section.title === "Checklist Assignment" ? (
                              <ChecklistAssignmentViewSection studyData={study} />
                            ) : section.title === "CRF Assignment" ? (
                              <CRFAssignmentViewSection studyData={study} />
                            ) : section.title === "Ambulatory Visit" ? (
                              <>
                                {!study?.visits || study.visits.length === 0 ? (
                                  <Col lg="12">
                                    <div className="text-muted text-center py-4">
                                      <i className="ri-information-line fs-20 align-middle me-2"></i>
                                      No Ambulatory Visits configured.
                                    </div>
                                  </Col>
                                ) : (
                                  study.visits.map((visit: any, index: number) => {
                                    const isExpanded = openVisitIndex === index;
                                    return (
                                      <React.Fragment key={visit.visitid || index}>
                                        <div className=" px-0" style={{borderRadius:"10px", backgroundColor: "#f7f9faff"}}>
                                        <Col lg="12">
                                          <div
                                            className="d-flex justify-content-between align-items-center px-3 py-2  border border-muted"
                                            onClick={() => setOpenVisitIndex(isExpanded ? null : index)}
                                            style={{ cursor: "pointer", userSelect: "none" , borderRadius: "10px"}}
                                            role="button"
                                          >
                                            <div className="d-flex align-items-center gap-2">
                                              <i className="ri-calendar-event-line text-primary fs-18" />
                                              <span className="fw-bold text-dark fs-14">
                                               {visit.visitname || "—"}
                                              </span>
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                              <i
                                                className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line text-muted fs-18`}
                                              />
                                            </div>
                                          </div>
                                        </Col>

                                        <Col lg="12">
                                          <Collapse isOpen={isExpanded} className="px-3">
                                            <Row className=" px-1" style={{borderRadius:"10px"}}>
                                              {[
                                                { key: "visitname", label: "Visit Name" },
                                                { key: "visittypename", label: "Visit Type" },
                                                { key: "visitcategoryname", label: "Visit Category" },
                                                { key: "scheduleddate", label: "Scheduled Date", isDate: true },
                                                { key: "visitstartday", label: "Window Start (Days)" },
                                                { key: "visitendday", label: "Window End (Days)" },
                                              ].map((f) => (
                                                <Col lg="3" key={f.key} className="mt-2">
                                                  <div className={viewStyles.reviewItem}>
                                                    <div className={viewStyles.reviewLabel}>{f.label}</div>
                                                    <div className={viewStyles.reviewValue}>
                                                      {f.isDate ? (
                                                        visit[f.key] ? formatDate(visit[f.key]) : "—"
                                                      ) : (
                                                        visit[f.key] !== undefined && visit[f.key] !== null && visit[f.key] !== "" ? visit[f.key] : "—"
                                                      )}
                                                    </div>
                                                  </div>
                                                </Col>
                                              ))}

                                              <Col lg="12" className="pt-1 pb-2">
                                                <div className="d-flex justify-content-between align-items-center">
                                                  <h6 className="text-muted fs-13">Visit Activities</h6>
                                                </div>
                                                <CommonTableIndexWise
                                                  moduleName={PERMISSION_MODULES.STUDY}
                                                  columns={activitycolumns}
                                                  data={visit.activitylist || []}
                                                  paginationShow={false}
                                                  hideAction={true}
                                                  showStatus={false}
                                                />
                                              </Col>
                                            </Row>
                                          </Collapse>
                                        </Col>
                                        </div>
                                      </React.Fragment>
                                    );
                                  })
                                )}
                              </>
                            ) : (
                              section.fields.map((key) => {
                              // Special handling for Investigational Product section
                              if (
                                section.title === "Investigational Product"
                              ) {
                                if (key === "testdrugname") {
                                  return (
                                    <React.Fragment key="test-product">
                                      <Col lg="12">
                                        <h6 className="text-primary mb-2 mt-2">
                                          Test Drug
                                        </h6>
                                      </Col>
                                      {[
                                        "testdrugname",
                                        "testdosageform",
                                        "teststrength",
                                        "testbatchno",
                                        "testmanufacturer",
                                        "testmanufacturingdate",
                                        "testexpirydate",
                                      ].map((testKey) => (
                                        <Col lg="3" key={testKey}>
                                          <div
                                            className={viewStyles.reviewItem}
                                          >
                                            <div
                                              className={
                                                viewStyles.reviewLabel
                                              }
                                            >
                                              {labels[
                                                testKey as keyof StudyFormValues
                                              ] || testKey}
                                            </div>
                                            <div
                                              className={
                                                viewStyles.reviewValue
                                              }
                                            >
                                              {study ? (
                                                truncateWithTooltip(
                                                  renderStudyValue(
                                                    testKey as keyof StudyFormValues,
                                                    study[
                                                    testKey as keyof StudyFormValues
                                                    ],
                                                  ),
                                                )
                                              ) : (
                                                <span className="text-muted">
                                                  —
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </Col>
                                      ))}
                                    </React.Fragment>
                                  );
                                } else if (key === "refdrugname") {
                                  return (
                                    <React.Fragment key="reference-product">
                                      <Col lg="12">
                                        <h6 className="text-primary mb-2 mt-3">
                                          Reference Drug
                                        </h6>
                                      </Col>
                                      {[
                                        "refdrugname",
                                        "refdosageform",
                                        "refstrength",
                                        "refbatchno",
                                        "refmanufacturer",
                                        "refmanufacturingdate",
                                        "refexpirydate",
                                      ].map((refKey) => (
                                        <Col lg="3" key={refKey}>
                                          <div
                                            className={viewStyles.reviewItem}
                                          >
                                            <div
                                              className={
                                                viewStyles.reviewLabel
                                              }
                                            >
                                              {labels[
                                                refKey as keyof StudyFormValues
                                              ] || refKey}
                                            </div>
                                            <div
                                              className={
                                                viewStyles.reviewValue
                                              }
                                            >
                                              {study ? (
                                                truncateWithTooltip(
                                                  renderStudyValue(
                                                    refKey as keyof StudyFormValues,
                                                    study[
                                                    refKey as keyof StudyFormValues
                                                    ],
                                                  ),
                                                )
                                              ) : (
                                                <span className="text-muted">
                                                  —
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </Col>
                                      ))}
                                    </React.Fragment>
                                  );
                                } else if (
                                  [
                                    "testdosageform",
                                    "teststrength",
                                    "testbatchno",
                                    "testmanufacturer",
                                    "testexpirydate",
                                    "refdosageform",
                                    "refstrength",
                                    "refbatchno",
                                    "refmanufacturer",
                                    "refexpirydate",
                                    "testmanufacturingdate",
                                    "refmanufacturingdate",
                                  ].includes(key as string)
                                ) {
                                  return null; // Already handled above
                                }
                              }

                              if (
                                key === "periods" &&
                                section.title === "Study Timeline"
                              ) {
                                return (
                                  <>
                                    <hr className="mb-1" />
                                    {study?.periods?.map(
                                      (p: any, i: number) => (
                                        <>
                                          <Col lg="3" key={`period-${i}`} >
                                            <div className={viewStyles.reviewItem}>
                                              <div className={viewStyles.reviewLabel} >
                                                Period
                                              </div>
                                              <div className={viewStyles.reviewValue} >
                                                Period {p.period_no}
                                              </div>
                                            </div>
                                          </Col>

                                          <Col lg="3" key={`period-${i}`}>
                                            <div className={viewStyles.reviewItem} >
                                              <div className={viewStyles.reviewLabel} >
                                                Period {p.period_no} Start Date
                                              </div>
                                              <div className={viewStyles.reviewValue} >
                                                {p.start_date ? formatDate(p.start_date) : "—"}
                                              </div>
                                            </div>
                                          </Col>

                                          <Col lg="3" key={`period-${i}`}>
                                            <div className={viewStyles.reviewItem} >
                                              <div className={viewStyles.reviewLabel} >
                                                Period {p.period_no} Check-in Date
                                              </div>
                                              <div className={viewStyles.reviewValue} >
                                                {p.checkin_date ? formatDate(p.checkin_date) : "—"}
                                              </div>
                                            </div>
                                          </Col>

                                          <Col lg="3" key={`period-${i}`}>
                                            <div className={viewStyles.reviewItem} >
                                              <div className={viewStyles.reviewLabel} >
                                                Period {p.period_no} Check-out Date
                                              </div>
                                              <div className={viewStyles.reviewValue} >
                                                {p.checkout_date ? formatDate(p.checkout_date) : "—"}
                                              </div>
                                            </div>
                                          </Col>
                                          {/* <Col lg="3" key={`period-${i}`}>
                                            <div className={viewStyles.reviewItem} >
                                              <div className={viewStyles.reviewLabel} >
                                                Period {p.period_no} Check-in Time
                                              </div>
                                              <div className={viewStyles.reviewValue} >
                                                {p.checkin_time ? p.checkin_time : "—"}
                                              </div>
                                            </div>
                                          </Col>
                                          <Col lg="3" key={`period-${i}`}>
                                            <div className={viewStyles.reviewItem} >
                                              <div className={viewStyles.reviewLabel} >
                                                Period {p.period_no} Check-out Time
                                              </div>
                                              <div className={viewStyles.reviewValue} >
                                                {p.checkout_time ? p.checkout_time : "—"}
                                              </div>
                                            </div>
                                          </Col> */}
                                          {
                                            study?.periods?.length !== i + 1 && <hr className="mb-0" />
                                          }
                                        </>
                                      ),
                                    )}
                                  </>
                                );
                              }

                              return (
                                <Col lg="3" key={key as string}>
                                  <div className={viewStyles.reviewItem}>
                                    <div className={viewStyles.reviewLabel}>
                                      {labels[key] || (key as string)}
                                    </div>
                                    <div className={viewStyles.reviewValue}>
                                      {study ? (
                                        truncateWithTooltip(
                                          renderStudyValue(key, study[key]),
                                        )
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                              );
                            }))}
                          </Row>
                        </CardBody>
                      </Card>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Container>
          {/* )} */}
        </div>
      </div>

      {/* Offcanvas Edit */}
      <CommonOffcanvasLayout
        isOpen={showEdit}
        toggle={() => toggleEdit(null)}
        title={`Edit ${activeSection}`}
        width={["Study Timeline", "Ambulatory Visit", "Checklist Assignment", "CRF Assignment"].includes(activeSection as string) ? "800px" : ""}
      >
        <EditStudyDetailsForm
          existingData={study}
          activeSection={activeSection}
          onSave={handleSave}
          onCancel={() => toggleEdit(null)}
        />
      </CommonOffcanvasLayout>

      {/* User verification Modal */}
      {isReasonModal && studyid != null && (
        <Reason
          isOpen={isReasonModal}
          toggle={toggleReasonModal}
          action="Update"
          id={studyid}
          click={btnReasonClick}
          isLoading={UpdateMutation.isPending}
        />
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <DeleteModal
          isopen={deleteModal}
          toggle={toggleDeleteModal}
          recordName={study?.studycode ?? undefined}
          recordId={studyid ?? 0}
          onDeleteClick={confirmDelete}
          showDeleteReason={showDeleteReason}
          toggleDeleteReason={toggleDeleteReason}
          isLoading={DeleteMutation.isPending}
        />
      )}
    </>
  );
};

export default ViewStudyDetails;
